// RECONCILING A LIST PRICE ONTO A LINE WHOSE SPLIT WAS NEVER RECORDED.
//
// ⚠️ AN ACCEPTED, NAMED EXCEPTION TO THE FRONTEND-CALCULATION RULE.
// CLAUDE.md says all pricing calculation belongs to /engine and that
// services/pricing.ts is a placeholder never to be extended. This file
// computes a percentage, so it has to say why it is not that.
//
// It is RECONCILIATION, not a step in the pricing chain: two values are
// already known -- the stored net price the engine actually priced from,
// and the list price a person has just typed -- and the discount that
// relates them is the single missing input. Solving `net = list x (1 - d)`
// for `d` recovers a fact that already exists; it does not decide a price.
//
// The chain produces IDENTICAL numbers either way. Nothing downstream reads
// `discountPct`: `unit_price` is what crosses the wire (netPrice, unmoved by
// this) and every engine output is computed from that. If this module were
// deleted, every price on every quote would be the same -- which is the test
// of whether a calculation belongs to the engine or not.
//
// THIS FILE LIVES OUTSIDE services/pricing.ts DELIBERATELY, at the owner's
// instruction, so the exception is visible as its own module rather than
// buried as one more line in the placeholder everything is told not to
// extend. Precedent: services/freightBasisGuard.ts and
// services/unpricedGuard.ts, both pure modules outside pricing.ts.
//
// ⚠️ WHAT THIS MODULE MUST NEVER GROW INTO. It may not compute a sell price,
// a margin, a landed cost, a freight or a duty figure, and it may not be
// called on the fresh-quote path where a discount is an INPUT somebody
// chose. It reconciles one recorded fact on one reopened line. Anything
// more belongs in /engine.

/** The outcome of reconciling a list price onto a line.
 *
 *  ⚠️ AN OPTIONAL-FIELD INTERFACE, NOT A DISCRIMINATED UNION, and for the
 *  same reason services/api.ts's PriceLinesResult is one: this project
 *  compiles with "strict": false, so without strictNullChecks TypeScript
 *  widens the `true`/`false` literals to `boolean` and a union NEVER narrows
 *  at the call site. Written as a union, `if (!outcome.ok)` would still
 *  refuse to see `reason`, which is exactly the compile error this shape
 *  avoids. */
export interface ReconcileResult {
  ok: boolean;
  /** The recovered discount, to one decimal place. Present only when ok. */
  discountPct?: number;
  /** Plain prose for the user. Present only when the edit is refused. */
  reason?: string;
}

/** One decimal place, matching DiscountRule.discountPct's documented shape. */
function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

/** Paise. The same rounding services/pricing.ts uses for money. */
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Does this line need a list price reconciled onto it?
 *
 *  ⚠️ THE NARROW PREDICATE, AND IT MUST STAY NARROW. The hazard is a line
 *  whose net price is ALREADY DISCOUNTED and whose split nobody recorded --
 *  i.e. a legacy line read back from a save. It is NOT "any line without a
 *  recorded split":
 *
 *    fresh picked line   no netPrice at all. Its discountPct came from a
 *                        rule and is an INPUT somebody chose; there is no
 *                        discounted figure to reconcile against and nothing
 *                        to apply twice. Must be left alone.
 *    reopened legacy     netPrice known, no list price, no recorded split.
 *                        THIS is the one.
 *    recorded line       the split is already known. Nothing to recover.
 *
 *  Widening this to `!discountRecorded` would catch the fresh line and change
 *  the fresh-quote path, which is out of scope for this slice by fence. */
export function needsDiscountReconcile(row: {
  netPrice?: number;
  listPrice?: number;
  discountRecorded?: boolean;
}): boolean {
  return (
    row.netPrice !== undefined &&
    row.listPrice === undefined &&
    !row.discountRecorded
  );
}

/** Recover the discount relating a stored net price to a typed list price.
 *
 *  ⚠️ netPrice IS NEVER RETURNED AND NEVER CHANGES. That is the point of the
 *  whole function: the stored net price is what the engine priced from and
 *  what is persisted as `unit_price`, so it must not move because somebody
 *  typed in a neighbouring cell. Only the missing percentage is produced.
 *  MaterialTable.discountReconcile.test.tsx proves it by measurement.
 *
 *  REFUSES rather than clamping, on all three failure paths. A clamped value
 *  would be written to the line and would then read exactly like a figure
 *  somebody chose -- the same "a guess that reads as authoritative" failure
 *  the backend's half-split refusal exists to prevent. */
export function reconcileDiscount(
  netPrice: number,
  listPrice: number,
): ReconcileResult {
  if (!Number.isFinite(listPrice) || listPrice <= 0) {
    return {
      ok: false,
      reason: "A list price must be greater than zero.",
    };
  }

  // ⚠️ REFUSED, NOT CLAMPED TO ZERO. A list price at or below the net price
  // implies a discount of zero or less -- i.e. a MARKUP -- and this column
  // does not express one. Writing 0.0% here would record "no discount was
  // granted", which is a real and different claim (it is exactly the state
  // discount_recorded exists to distinguish), so it must not be produced by
  // a number that actually means the opposite.
  if (listPrice <= netPrice) {
    return {
      ok: false,
      reason:
        `A list price of ${listPrice} is not above this line's net price of ` +
        `${netPrice}, so it implies a markup rather than a discount. ` +
        `Enter the OEM list price this line was discounted from.`,
    };
  }

  const discountPct = round1((1 - netPrice / listPrice) * 100);

  // ⚠️ THE PIN: THE ROUNDED PERCENTAGE MUST REPRODUCE THE NET PRICE EXACTLY.
  //
  // WHY THIS GUARD EXISTS AT ALL -- it closes the hole in the other three.
  //
  // "Round to 1dp" and "leave netPrice unmoved" are not automatically
  // compatible, and nothing above enforces the second. recomputeAll
  // re-derives net from list x (1 - d/100), so a percentage that rounds away
  // from the true ratio reproduces a DIFFERENT net price -- and it does not
  // move here, where someone is looking at the cell they just edited. It
  // moves on the NEXT edit of that line, silently, long after anyone
  // connected the two.
  //
  // That is precisely the failure back-derivation was chosen to prevent. The
  // alternative to reconciling was to re-state the line (discountPct = 0,
  // netPrice = listPrice), and it was rejected because it moves a saved
  // quote's cost as a side effect of a UI edit. A 1dp rounding that drifts
  // does the same thing, just later and more quietly -- so accepting it here
  // would reintroduce the rejected behaviour through the back door.
  //
  // Rather than accept that drift or abandon 1dp, the mismatch is REFUSED.
  // It is close to unreachable in practice: real list prices and real
  // negotiated discounts land on clean steps, and the legacy lines this
  // exists for were generated from integer percentages -- 900 round-trip
  // cases over the real data shape produce zero mismatches. When it does
  // happen the list price is genuinely inconsistent with the stored net
  // price, and saying so is more useful than absorbing it.
  if (round2(listPrice * (1 - discountPct / 100)) !== round2(netPrice)) {
    return {
      ok: false,
      reason:
        `A list price of ${listPrice} implies a discount of ` +
        `${((1 - netPrice / listPrice) * 100).toFixed(4)}%, which does not ` +
        `round to a 0.1% step without changing this line's net price of ` +
        `${netPrice}. The net price is what this quote was priced from and ` +
        `is not changed here. Check the list price.`,
    };
  }

  return { ok: true, discountPct };
}
